import React, { useState, useMemo, useEffect, useRef } from "react";
import "./Tabs.css";
import DimensionsTab from "src/components/Tabs/DimensionsTab/DimensionsTab";
import WallsTab from "src/components/Tabs/WallsTab/WallsTab";
import RoofingTab from "src/components/Tabs/RoofingTab/RoofingTab";
import MaterialsListTab from "src/components/Tabs/MaterialsListTab/MaterialsListTab";
import OptiesTab from "src/components/Tabs/OptiesTab/OptiesTab";
import StorageTab from "src/components/Tabs/StorageTab/StorageTab";
import { useView } from "src/contexts/ViewContext";
import RoofTypeTab from "src/components/Tabs/RoofTypeTab/RoofTypeTab";
import { useConfiguration } from "src/contexts/ConfigurationContext";
// @ts-ignore
import { getConfigurationHash } from "src/helpers/configuration";
import { useWindowResize } from "src/hooks/useWindowResize";
import { isRestrictedRoofType } from "src/helpers/roofType";

interface TabConfig {
  id: number;
  title: string;
  component: React.ReactNode;
}

interface TabProps {
  tab: {
    id: number;
    title: string;
    component: React.ReactNode;
  };
  displayIndex: number;
  isActive: boolean;
  onClick: () => void;
  onNext?: (currentTabId: number) => void;
  onPrev?: (currentTabId: number) => void;
  hidden: boolean;
  isCompleted: boolean;
}

const tabsData: TabConfig[] = [
  {
    id: 1,
    title: "Type overkapping",
    component: <RoofTypeTab />,
  },
  {
    id: 2,
    title: "Afmetingen",
    component: <DimensionsTab />,
  },
  {
    id: 3,
    title: "Dakbedekking",
    component: <RoofingTab />,
  },
  {
    id: 4,
    title: "Berging",
    component: <StorageTab />,
  },
  {
    id: 5,
    title: "Wanden",
    component: <WallsTab />,
  },
  {
    id: 6,
    title: "Opties",
    component: <OptiesTab />,
  },
  {
    id: 7,
    title: "Materiaallijst",
    component: <MaterialsListTab />,
  },
];

const ArrowIcon: React.FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
  >
    <path
      d="M16 12L10 6L8.59 7.41L13.17 12L8.59 16.59L10 18L16 12Z"
      fill="currentColor"
    />
  </svg>
);

const PrevIcon: React.FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="55"
    height="45"
    viewBox="0 0 55 45"
    fill="none"
  >
    <path
      d="M32.0115 14.5898L23.8801 21.394C23.1454 22.0088 23.1454 22.9912 23.8801 23.606L32.0115 30.4102"
      stroke="currentColor"
      strokeWidth="2.14844"
      strokeLinecap="round"
    />
  </svg>
);

const NextIcon: React.FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="12"
    height="19"
    viewBox="0 0 12 19"
    fill="none"
  >
    <path
      d="M1.98849 17.4102L10.1199 10.606C10.8546 9.9912 10.8546 9.0088 10.1199 8.39398L1.98849 1.58984"
      stroke="currentColor"
      strokeWidth="2.14844"
      strokeLinecap="round"
    />
  </svg>
);

const CheckIcon: React.FC = () => <span>✓</span>;

interface TooltipProps {
  completed: boolean;
}

const Tab: React.FC<TabProps> = ({
  tab,
  displayIndex,
  isActive,
  onClick,
  hidden,
  onNext,
  onPrev,
  isCompleted,
}) => {
  if (hidden) return null;

  return (
    <div className={`tab${isActive ? " open" : ""}`} id={`tab-${tab.id}`}>
      <div
        className={`tab-header ${isActive ? "open" : ""} ${
          isCompleted ? "completed" : ""
        }`}
        onClick={onClick}
      >
        {!isCompleted && (
          <span id={`tab-number-${tab.id}`}>{displayIndex}</span>
        )}
        {isCompleted && <CheckIcon />}
        <span>{tab.title}</span>
        <ArrowIcon />
      </div>
      {isActive && (
        <div className="tab-content">
          {tab.component}
          <div className="tab-navigation">
            {onPrev && (
              <button
                title="Vorige tab"
                className={"prevButton"}
                onClick={() => {
                  onPrev(tab.id);
                }}
              >
                <NextIcon />
                Vorige
              </button>
            )}
            {onNext && (
              <button
                className={"nextButton"}
                onClick={() => {
                  onNext(tab.id);
                }}
              >
                Volgende
                <NextIcon />
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const Tabs: React.FC = () => {
  const windowWidth = useWindowResize();
  const configurationHash = getConfigurationHash();
  const [activeTab, setActiveTab] = useState<number | null>(null);
  const [completedTabs, setCompletedTabs] = useState<Set<number>>(
    new Set(configurationHash ? tabsData.map((t) => t.id) : [])
  );
  const { isAdmin } = useView();
  const { state, configurator, setUncompletedTabs } = useConfiguration();
  const lastClickWasWanden = useRef<boolean>(false);

  const scrollWithinContainer = (
    element: HTMLElement,
    block: ScrollLogicalPosition = "start"
  ) => {
    const scrollContainer =
      element.closest(".scroll") ||
      (document.querySelector(".scroll") as HTMLElement | null);

    if (!scrollContainer) {
      element.scrollIntoView({ behavior: "smooth", block });
      return;
    }

    const containerRect = scrollContainer.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();

    let delta = elementRect.top - containerRect.top;
    if (block === "center") {
      delta -= scrollContainer.clientHeight / 2 - elementRect.height / 2;
    }

    scrollContainer.scrollTo({
      top: scrollContainer.scrollTop + delta,
      behavior: "smooth",
    });
  };

  const visibleTabs = useMemo(() => {
    return tabsData.filter((tab) => {
      if (tab.id === 7) {
        return isAdmin();
      }

      if (
        isRestrictedRoofType(state.roofType) &&
        (tab.id === 3 || tab.id === 4 || tab.id === 5)
      ) {
        return false;
      }

      return true;
    });
  }, [isAdmin, state.roofType]);

  const handleOutWandenClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const wandenElement = document.getElementById("tab-5");
    const clickedInsideTab5 = wandenElement
      ? wandenElement.contains(e.target as Node)
      : false;

    if (clickedInsideTab5) {
      lastClickWasWanden.current = true;
    } else {
      if (lastClickWasWanden.current) {
        configurator.resetCamera();
      }

      lastClickWasWanden.current = false;
    }
  };

  const handleTabClick = (id: number) => {
    onTabChange(id);
    setActiveTab((prevActive) => (prevActive === id ? null : id));
  };

  const goToNextTab = (currentTabId: number) => {
    const currentIndex = visibleTabs.findIndex((t) => t.id === currentTabId);
    if (currentIndex < 0) return;
    const nextTab = visibleTabs[currentIndex + 1];

    if (currentTabId === 5) {
      configurator.resetCamera();
    }

    setCompletedTabs((prev) => {
      const newSet = new Set(prev);
      newSet.add(currentTabId);
      return newSet;
    });

    if (!nextTab) {
      setActiveTab(null);
      return;
    }

    setActiveTab(nextTab.id);
    onTabChange(nextTab.id);
  };

  const goToPrevTab = (currentTabId: number) => {
    const currentIndex = visibleTabs.findIndex((t) => t.id === currentTabId);
    if (currentIndex < 0) return;
    const prevTab = visibleTabs[currentIndex - 1];

    if (currentTabId === 5) {
      configurator.resetCamera();
    }

    if (prevTab !== undefined && prevTab.id !== undefined) {
      setActiveTab(prevTab.id);

      if (completedTabs.has(prevTab.id)) {
        const newSet = new Set(completedTabs);
        newSet.delete(prevTab.id);
        setCompletedTabs(newSet);
      }
      onTabChange(prevTab.id);
    }
  };

  const onTabChange = (tabId: number) => {
    setTimeout(() => {
      configurator.deselectWalls();
      configurator.deselectBerging();
      const tabElement = document.querySelector(
        `#tab-${tabId}`
      ) as HTMLElement | null;
      if (tabElement) {
        scrollWithinContainer(tabElement);
      }
    }, 80);
  };

  useEffect(() => {
    if (activeTab === null) return;
    if (visibleTabs.some((tab) => tab.id === activeTab)) return;
    setActiveTab(null);
  }, [activeTab, visibleTabs]);

  useEffect(() => {
    const uncompletedTabs = new Set(
      visibleTabs
        .filter((t) => !completedTabs.has(t.id) && t.id !== 7)
        .map((t) => t.id)
    );
    setUncompletedTabs(uncompletedTabs);
  }, [activeTab, completedTabs, setUncompletedTabs, visibleTabs]);

  // useEffect(() => {
  //   const id = activeTab ?? 0;
  //   if (!id) return;
  //   const tabElement = document.querySelector(`#tab-${id}`) as HTMLElement;
  //   const scroll = document.querySelector(`.scroll`) as HTMLElement | null;
  //   const offsetTop = window.innerWidth <= 768 ? 0 : 52;
  //   const canScroll = scroll && scroll.scrollHeight > scroll.clientHeight + 1;
  //   if (scroll && tabElement && canScroll) {
  //     scroll.scrollTo({
  //       top: tabElement.offsetTop - offsetTop,
  //       behavior: "smooth",
  //     });
  //   }
  // }, [activeTab]);

  return (
    <>
      <div className="scroll">
        <div className="tabs-container" onClick={handleOutWandenClick}>
          {visibleTabs.map((tab, index) => {
            const isActive = activeTab === tab.id;
            const isCompleted = completedTabs.has(tab.id);
            const hasPrev = index > 0 && isActive && tab.id !== 7;

            return (
              <Tab
                key={`main-tab-${tab.id}`}
                tab={tab}
                displayIndex={index + 1}
                isActive={isActive}
                onClick={() => handleTabClick(tab.id)}
                hidden={false}
                onNext={tab.id !== 7 ? goToNextTab : undefined}
                onPrev={isActive && hasPrev ? goToPrevTab : undefined}
                isCompleted={isCompleted}
              />
            );
          })}
        </div>
      </div>
    </>
  );
};

export default Tabs;
